import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useRoute, useLocation } from "wouter";
import { CircleCheck } from "lucide-react";
import { z } from "zod";
import { resetPasswordSchema } from "@shared/validation/auth.schema";
import { useResetPassword } from "../lib/api/usePassword";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AuthHero } from "../components/AuthHero";
import { BrandWordmark } from "../components/BrandWordmark";
import { Button } from "../components/ui/Button";
import { Field } from "../components/ui/Field";
import { Reveal } from "../components/ui/Reveal";

/**
 * The token comes from the URL, not the form, so the form only collects the
 * password — and a confirmation, which the API has no reason to receive.
 */
const formSchema = resetPasswordSchema
  .omit({ token: true })
  .extend({ confirm: z.string() })
  .refine((v) => v.password === v.confirm, {
    message: "Passwords don't match",
    path: ["confirm"],
  });

type FormValues = z.infer<typeof formSchema>;

export function ResetPassword() {
  const [, params] = useRoute("/reset-password/:token");
  const [, setLocation] = useLocation();
  const reset = useResetPassword();
  const token = params?.token ?? "";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  return (
    <main className="min-h-screen lg:grid lg:grid-cols-2">
      <AuthHero />
      <div className="flex min-h-screen flex-col bg-white">
        <header className="px-6 py-5 lg:hidden">
          <Link href="/" className="inline-flex items-center gap-2">
            <BrandWordmark className="h-6" />
          </Link>
        </header>
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <Reveal className="w-full max-w-sm">
            {reset.isSuccess ? (
              <>
                <CircleCheck className="h-8 w-8 text-emerald-600" aria-hidden />
                <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-neutral-900">
                  Password changed
                </h1>
                <p className="mt-1.5 text-sm text-neutral-600">
                  You&apos;ve been signed out everywhere else, so anyone using the old
                  password no longer has access.
                </p>
                <Button className="mt-5 w-full" onClick={() => setLocation("/login")}>
                  Sign in
                </Button>
              </>
            ) : (
              <>
                <h1 className="font-display text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
                  Choose a new password
                </h1>
                <p className="mt-1.5 text-sm text-neutral-600">
                  At least 10 characters. This link works once.
                </p>
                <form
                  className="mt-6 space-y-4"
                  onSubmit={handleSubmit((v) => reset.mutate({ token, password: v.password }))}
                  noValidate
                >
                  <Field label="New password" htmlFor="new-password" error={errors.password?.message}>
                    <input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      autoFocus
                      {...register("password")}
                      className={INPUT_CLASS}
                    />
                  </Field>
                  <Field label="Confirm password" htmlFor="confirm-password" error={errors.confirm?.message}>
                    <input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      {...register("confirm")}
                      className={INPUT_CLASS}
                    />
                  </Field>
                  {reset.isError && (
                    <p role="alert" className="text-sm font-medium text-red-700">
                      {reset.error.code === "RATE_LIMITED"
                        ? "Too many attempts — please wait a while and try again."
                        : "This reset link is invalid or has expired. Request a new one."}
                    </p>
                  )}
                  <Button type="submit" className="w-full" isLoading={reset.isPending}>
                    Set new password
                  </Button>
                </form>
                <p className="mt-6 text-sm text-neutral-600">
                  <Link href="/forgot-password" className="font-medium text-sky-700 hover:underline">
                    Request a new link
                  </Link>
                </p>
              </>
            )}
          </Reveal>
        </div>
      </div>
    </main>
  );
}
